import {FormEvent, useEffect, useState} from "react";
import {Button, Form, Modal, ProgressBar} from "react-bootstrap";
import {ErrorAlert} from "./ErrorAlert";
import {SubmitButton} from "./SubmitButton";
import type {UploadedImage} from "./RichTextEditor";

const TYPES = ['image/png', 'image/jpeg'];
const MAX_BYTES = 10 * 1024 * 1024;

interface Props {
    show: boolean;
    // An image already in the text: only its description changes.
    editing?: { alt: string };
    onUpload(file: File, onProgress: (fraction: number) => void): Promise<UploadedImage>;
    onInsert(image: UploadedImage, alt: string): void;
    onDescribe(alt: string): void;
    onClose(): void;
}

// Adds an image to RichTextEditor's text (uploading it), or changes an image's description. Every image needs a
// description, for screen reader users and for when it can't be shown.
export function ImageDialog({show, editing, onUpload, onInsert, onDescribe, onClose}: Props) {
    const [file, setFile] = useState<File>();
    const [alt, setAlt] = useState('');
    const [validated, setValidated] = useState(false);
    const [progress, setProgress] = useState<number>();
    const [error, setError] = useState<unknown>();

    useEffect(() => {
        if (show) {
            setFile(undefined);
            setAlt(editing?.alt ?? '');
            setValidated(false);
            setProgress(undefined);
            setError(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const fileProblem = editing ? undefined
        : !file ? 'Choose a PNG or JPEG image.'
        : !TYPES.includes(file.type) ? 'The image must be a PNG or JPEG.'
        : file.size > MAX_BYTES ? 'The image must be 10 MB or smaller.'
        : undefined;
    const altProblem = alt.trim() ? undefined : 'Describe the image.';

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        // The dialog is in a portal, but React still bubbles its submit to a form around the editor.
        e.stopPropagation();
        setValidated(true);
        if (fileProblem || altProblem) {
            return;
        }
        if (editing) {
            onDescribe(alt.trim());
            return;
        }
        setError(undefined);
        setProgress(0);
        try {
            onInsert(await onUpload(file!, setProgress), alt.trim());
        } catch (err) {
            setError(err);
        } finally {
            setProgress(undefined);
        }
    };

    const busy = progress !== undefined;
    return (
        <Modal show={show} onHide={busy ? undefined : onClose} aria-labelledby="image-dialog-title">
            <Form noValidate onSubmit={submit}>
                <Modal.Header closeButton={!busy}>
                    <Modal.Title id="image-dialog-title" className="h5">{editing ? 'Image description' : 'Add an image'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error}/>
                    {!editing && (
                        <Form.Group className="mb-3" controlId="image-dialog-file">
                            <Form.Label>Image</Form.Label>
                            <Form.Control type="file" accept={TYPES.join(',')} disabled={busy}
                                          isInvalid={validated && !!fileProblem} aria-describedby="image-dialog-file-help"
                                          onChange={e => setFile((e.target as HTMLInputElement).files?.[0])}/>
                            <Form.Control.Feedback type="invalid">{fileProblem}</Form.Control.Feedback>
                            <Form.Text id="image-dialog-file-help">PNG or JPEG, up to 10 MB. Screenshots work best as PNG.</Form.Text>
                        </Form.Group>
                    )}
                    <Form.Group controlId="image-dialog-alt">
                        <Form.Label>Description</Form.Label>
                        <Form.Control as="textarea" rows={2} value={alt} disabled={busy} isInvalid={validated && !!altProblem}
                                      aria-describedby="image-dialog-alt-help" onChange={e => setAlt(e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{altProblem}</Form.Control.Feedback>
                        <Form.Text id="image-dialog-alt-help">
                            What the image shows, for people who can't see it. For example: “The filter section, with Cutoff turned halfway up.”
                        </Form.Text>
                    </Form.Group>
                    {busy && <ProgressBar now={progress * 100} className="mt-3" aria-label="Uploading"/>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                    <SubmitButton busy={busy}>{editing ? 'Save' : 'Add image'}</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
